import { IVariant, UnleashClient } from 'unleash-proxy-client'

import { getUserGroupMembership } from '~/notes/designExploration/api/groupMembership'
import { getGroupIds } from '~/notes/utils/cookies'

import { getCustomer } from './getCustomer'
import { getEnv } from './getEnv'
import { UnleashFlagValue } from './types'

const getUnleashApiToken = (envLabel: string): string => {
  if (envLabel.includes('Prod')) {
    return 'default:production.00e880133a9aa60d27d5d8ae04cb13c5b6d806cabf0384e3c12b4483'
  }
  if (envLabel.includes('Test')) {
    return 'default:development.7692bb98fac18f061f93b002031808628747a9295002a98506cd590e'
  }
  return 'default:development.unleash-insecure-api-fe-token'
}

const unleashEnv = getEnv()
const unleashUrl = `${window.location.origin}/unleash/api/frontend`
const unleashConfig = {
  url: unleashUrl,
  clientKey: getUnleashApiToken(unleashEnv),
  // Refresh is turned off. Changing flags while the app is running may cause unpredictable behavior.
  refreshInterval: 0,
  appName: 'regard-app-fe',
  // groupId starts empty so strategies with a groupId constraint don't vacuously pass before
  // FlagProvider sets the real value from the live API.
  context: { properties: { customer: getCustomer(), groupId: '' } },
}

export const unleashClient = new UnleashClient(unleashConfig)

// Set of flag names enabled for any of the user's group IDs beyond the first, populated at startup.
// The first group is set on the main unleashClient context before start(), so the proxy server
// evaluates its IN/NOT_IN constraints server-side.
const groupEnabledFlags = new Set<string>()

type Toggle = { name: string; enabled: boolean }

const fetchTogglesForGroup = async (
  groupId: string,
  userId: string,
  token: string,
): Promise<Toggle[]> => {
  const params = new URLSearchParams({
    appName: 'regard-app-fe',
    userId,
    'properties[customer]': getCustomer(),
    'properties[groupId]': groupId,
  })
  try {
    const resp = await fetch(`${unleashUrl}?${params}`, {
      headers: { Authorization: token },
    })
    if (!resp.ok) return []
    const data = (await resp.json()) as { toggles: Toggle[] }
    return data.toggles
  } catch {
    return []
  }
}

/**
 * Fetches the user's group IDs from the live API, falling back to the cookie on failure.
 * Called by FlagProvider before unleashClient.start() so the real groupId can be set on
 * the client context before the initial /api/frontend fetch goes out.
 */
export const fetchGroupIds = async (): Promise<string[]> => {
  try {
    const membership = await getUserGroupMembership()
    return membership.groups.map((g) => g.groupId)
  } catch {
    return getGroupIds()
  }
}

/**
 * Fetches toggles for group IDs beyond the first (groups[1..n]) and populates groupEnabledFlags.
 * Accepts the pre-fetched groupIds from FlagProvider to avoid a second API call.
 * The first group is already set on the main unleashClient context, so only extra groups need
 * per-group fetches.
 */
export const loadGroupScopedFlags = async (
  userId: string,
  groupIds: string[],
): Promise<void> => {
  const extraGroupIds = groupIds.slice(1)
  if (extraGroupIds.length === 0) return

  const token = getUnleashApiToken(getEnv())
  const results = await Promise.all(
    extraGroupIds.map((gid) => fetchTogglesForGroup(gid, userId, token)),
  )
  results.flat().forEach((toggle) => {
    if (toggle.enabled) {
      groupEnabledFlags.add(toggle.name)
    }
  })
}

/** Returns true if the flag is enabled for any of the user's additional group IDs. */
export const isGroupFlagEnabled = (flagName: string): boolean =>
  groupEnabledFlags.has(flagName)

/** Clears all group flag state. Exposed for testing only. */
export const _resetGroupFlagsForTesting = (): void => {
  groupEnabledFlags.clear()
}

export const VALUE_VARIANT_NAME = 'value'

export const flagHasVariant = (variant: IVariant) =>
  variant &&
  variant.enabled &&
  variant.feature_enabled &&
  variant.name === VALUE_VARIANT_NAME &&
  variant.payload?.type === 'json'

const resolveValueFromJSON = <K extends keyof Flags>(
  flagKey: K,
  json: string,
): UnleashFlagValue => {
  const payload = JSON.parse(json)
  const payloadValueType = payload.type
  const payloadValue = payload.value

  if (payloadValueType === 'boolean') {
    if (payloadValue === 'true') return true
    if (payloadValue === 'false') return false
    return !!payloadValue
  }
  if (payloadValueType === 'string') {
    return payloadValue
  }
  if (payloadValueType === 'number') {
    const value = parseFloat(payloadValue)
    if (Number.isNaN(value)) {
      throw new Error(`Number payload value not a number ${payloadValue}`)
    }
    return parseFloat(payloadValue)
  }

  throw new Error(
    `Unknown payload type ${payloadValueType} with value "${payloadValue}" for flag ${flagKey}`,
  )
}

/**
 * Gets an unleash flag's value following the convention in
 * https://regard.atlassian.net/wiki/spaces/HE/pages/1860272137/Feature+Flags#Payload-structure
 */
export const getFlagVariantValue = <K extends keyof Flags>(
  flagKey: K,
  variant: IVariant,
): Flags[K] | undefined => {
  if (!variant.payload) {
    return undefined
  }

  try {
    const json = variant.payload.value
    const value = resolveValueFromJSON(flagKey, json)
    // Hard cast to resolve UnleashFlagValue to Flags[K]
    return value as Flags[K]
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('error getting variant payload data', e)
    return undefined
  }
}

export { UnleashClient }
